import type { PlayerReportInput, PlayerReportResult } from '@/types';
import { ApiError } from '@/features/bet-inspection/lib/api';

/** POST {api}/player-reports — per-player bet/win/GGR for a period. */
export async function fetchPlayerReport(params: {
  api: string;
  token: string | null;
  input: PlayerReportInput;
  signal?: AbortSignal;
}): Promise<PlayerReportResult> {
  const base = params.api.replace(/\/+$/, '');

  let res: Response;
  try {
    res = await fetch(`${base}/player-reports`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(params.token ? { authorization: `Bearer ${params.token}` } : {}),
      },
      body: JSON.stringify(params.input),
      signal: params.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError(
      `Could not reach the betlens API at ${base}. Check the API base URL, that the ` +
        `backend is running, and that it allows this origin (CORS).`,
      0,
    );
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as PlayerReportResult;
}

/** GET {api}/base-currencies — currencies a report can be converted INTO. */
export async function fetchBaseCurrencies(params: {
  api: string;
  token: string | null;
  signal?: AbortSignal;
}): Promise<string[]> {
  const base = params.api.replace(/\/+$/, '');
  const res = await fetch(`${base}/base-currencies`, {
    headers: params.token ? { authorization: `Bearer ${params.token}` } : {},
    signal: params.signal,
  }).catch(() => {
    throw new ApiError(`Could not load base currencies from ${base}.`, 0);
  });
  if (!res.ok) throw new ApiError(`Could not load base currencies (${res.status})`, res.status);
  const body = (await res.json()) as { currencies?: string[] };
  return body.currencies ?? [];
}

/** GET {api}/player-reports/currencies — currencies a project has activity in. */
export async function fetchReportCurrencies(params: {
  api: string;
  token: string | null;
  playServiceId: string;
  signal?: AbortSignal;
}): Promise<string[]> {
  const base = params.api.replace(/\/+$/, '');
  const res = await fetch(
    `${base}/player-reports/currencies?play_service_id=${encodeURIComponent(params.playServiceId)}`,
    {
      headers: params.token ? { authorization: `Bearer ${params.token}` } : {},
      signal: params.signal,
    },
  ).catch(() => {
    throw new ApiError(`Could not load currencies from ${base}.`, 0);
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? `Could not load currencies (${res.status})`, res.status);
  }
  const body = (await res.json()) as { currencies?: string[] };
  return body.currencies ?? [];
}
